'use client'

import { useCallback, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/providers/AuthContext'
import { readPref, removePrefs } from '@/lib/prefs/store'
import {
  PROFILE_AVATAR_COLOR_KEY,
  PROFILE_AVATAR_ICON_KEY,
  PROFILE_AVATAR_KEYS,
  toProfileAvatarColor,
  toProfileAvatarIcon,
} from '@/lib/prefs/profileAvatar'
import { meKeys } from '@/hooks/useMe'
import { updateMe } from '@/services/users/me'
import type { UserAvatar } from '@/types/UserAvatar'

export interface UseProfileAvatarResult {
  /** Slug lucide KEBAB, ou null (→ iniciais). */
  icon: string | null
  /** `#rrggbb` minusculo, ou null (→ gradiente default). */
  color: string | null
  isDefault: boolean
  setIcon: (slug: string | null) => void
  setColor: (hex: string | null) => void
  restoreDefaults: () => void
}

/** Os dois eixos anulam JUNTOS: `{icon: null, color: null}` seria um avatar vazio
 *  que o back teria que interpretar — `null` já é "sem avatar". */
function proximoAvatar(icon: string | null, color: string | null): UserAvatar | null {
  return icon === null && color === null ? null : { icon, color }
}

export function useProfileAvatar(): UseProfileAvatarResult {
  const { user, refresh } = useAuth()
  const qc = useQueryClient()
  const icon = toProfileAvatarIcon(user?.avatar?.icon)
  const color = toProfileAvatarColor(user?.avatar?.color)

  const salvar = useMutation({
    mutationFn: (avatar: UserAvatar | null) => updateMe({ avatar }),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: meKeys.me })
      await refresh() // o avatar mora no /auth/me: sem isto a rail fica com o antigo
    },
    // Erro NUNCA engolido: sem aviso o clique some sem explicação e o usuário repete.
    onError: () => toast.error('Não foi possível salvar o avatar.'),
  })

  const setIcon = useCallback(
    (slug: string | null) => {
      // Recusa é NO-OP, não limpeza: escolher um ícone impersistível mantém o que
      // já valia. Mesmo coercitor da leitura, pelo mesmo motivo de antes — o back
      // recusa slug > 32 chars com 422, e 37 dos 3598 nomes do IconPicker caem ali.
      const proximo = slug === null ? null : toProfileAvatarIcon(slug)
      if (slug !== null && proximo === null) return
      salvar.mutate(proximoAvatar(proximo, color))
    },
    [color, salvar],
  )
  const setColor = useCallback(
    (hex: string | null) => {
      const proximo = hex === null ? null : toProfileAvatarColor(hex)
      if (hex !== null && proximo === null) return
      salvar.mutate(proximoAvatar(icon, proximo))
    },
    [icon, salvar],
  )
  const restoreDefaults = useCallback(() => salvar.mutate(null), [salvar])

  /* Migração ÚNICA do localStorage → back. Roda no primeiro mount em que o back
     diz "sem avatar" E há preferência local válida: sobe o que a pessoa já tinha
     escolhido e apaga as chaves. `useRef` porque o effect roda 2x em StrictMode e
     o guard não pode depender do resultado do PATCH (que é assíncrono).
     Depois disto `PROFILE_AVATAR_KEYS` não é mais escrita por ninguém. */
  const migrou = useRef(false)
  useEffect(() => {
    if (migrou.current || !user || user.avatar) return
    const local = proximoAvatar(
      toProfileAvatarIcon(readPref(user.user_id, PROFILE_AVATAR_ICON_KEY)),
      toProfileAvatarColor(readPref(user.user_id, PROFILE_AVATAR_COLOR_KEY)),
    )
    if (!local) return
    migrou.current = true
    updateMe({ avatar: local })
      .then(() => {
        removePrefs(user.user_id, PROFILE_AVATAR_KEYS)
        return refresh()
      })
      // Falhou: as chaves ficam, e a próxima sessão tenta de novo. Apagar antes de
      // confirmar perderia a escolha do usuário num 500.
      .catch(() => {
        migrou.current = false
      })
  }, [user, refresh])

  return {
    icon,
    color,
    /** Agora é o ESTADO DO BACK ("sem avatar"), não mais o storage cru — a entrada
     *  morta que o `isDefault` antigo destravava não existe mais depois da migração. */
    isDefault: icon === null && color === null,
    setIcon,
    setColor,
    restoreDefaults,
  }
}
